import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
  BusinessNeedIntentOptions,
  BusinessNeedOptions,
  ContactFormFields,
  isPersonalEmail,
  MAX_LENGTH,
  sanitizeString,
  type ContactFormFields as TContactFormFields,
} from '@transcend-io/contact-form-schema';
import { snakeCase } from 'change-case';
import { AnimatePresence, motion } from 'framer-motion';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { FormProvider, type SubmitHandler } from 'react-hook-form';
import { z } from 'zod';

import {
  ChiliPiperProvider,
  SchedulingStateEnum,
  useChiliPiperModal,
} from '#/lib/chili-piper/ChiliPiperContext';
import { CONTACT_CHILI_PIPER_ROUTER_ID } from '#/lib/chili-piper/constants';
import { scheduleAfterSubmit } from '#/lib/chili-piper/scheduleAfterSubmit';
import { useConsentManager } from '#/lib/consent-manager';
import {
  clearPendingContactSubmission,
  setPendingContactSubmission,
  usePendingContactSubmission,
} from '#/lib/contact-form-client/pending-submission';
import { submitLead } from '#/lib/contact-form-client/submit';
import type { SanityContactForm } from '#/lib/sanity/queries/form';
import { useAnalytics, useConversionEvent } from '#/lib/segment';
import { cn } from '#/lib/utils';
import { BasicText } from '#/components/BasicText';
import { Button } from '#/components/Button';
import { Input } from '#/components/Input';
import { Listbox } from '#/components/Listbox';
import { Textarea } from '#/components/Textarea';

import LoadingSpinner from '../LoadingSpinner';
import { FormConfirmation } from './Custom/Confirmation';
import { FormErrorMessage } from './Custom/ErrorMessage';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './FormControls';
import { generateAnalyticsId, useFormMetadata } from './utils';

const requiredText = (label: string) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(MAX_LENGTH.text, {
      error: `${label} must be ${MAX_LENGTH.text} characters or fewer.`,
    })
    .transform(sanitizeString);

const contactFieldsSchema = ContactFormFields.extend({
  email: ContactFormFields.shape.email.refine(
    (value) => !isPersonalEmail(value),
    {
      message: 'Please enter a work email address.',
    },
  ),
  firstName: requiredText('First name'),
  lastName: requiredText('Last name'),
  title: requiredText('Job title'),
});

const followUpSchema = ContactFormFields.extend({
  businessNeeds: z
    .array(z.enum(BusinessNeedOptions))
    .min(1, 'Please select at least one option'),
  businessNeedIntent: z.enum(BusinessNeedIntentOptions, {
    error: 'Please select an option',
  }),
});

type Stage = 'fields' | 'scheduling' | 'questions' | 'confirmation';

type Props = {
  form: SanityContactForm;
  confirmation: React.ComponentProps<typeof FormConfirmation>['confirmation'];
  theme?: 'light' | 'dark';
  buttonTheme?: 'light' | 'dark';
  className?: string;
  onSubmitSuccess?: () => void;
  onSubmitError?: () => void;
};

function ContactFormInner(props: Props) {
  const pending = usePendingContactSubmission();
  const [stage, setStage] = useState<Stage>('fields');
  const [submissionId, setSubmissionId] = useState<string | undefined>(
    pending?.submissionId,
  );
  const [email, setEmail] = useState(pending?.email ?? '');

  useEffect(() => {
    if (!pending) {
      return;
    }
    setSubmissionId(pending.submissionId);
    setEmail(pending.email);
  }, [pending]);

  return (
    <div className={cn('relative', props.className)}>
      <AnimatePresence mode="wait">
        {stage === 'confirmation' ? (
          <motion.div
            key="confirmation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <FormConfirmation
              confirmation={props.confirmation}
              theme={props.buttonTheme ?? props.theme}
            />
          </motion.div>
        ) : stage === 'questions' && submissionId && email ? (
          <motion.div
            key="questions"
            className="w-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <FollowUpQuestions
              form={props.form}
              theme={props.theme}
              email={email}
              submissionId={submissionId}
              onSubmitSuccess={() => {
                clearPendingContactSubmission();
                setStage('confirmation');
                props.onSubmitSuccess?.();
              }}
              onSubmitError={props.onSubmitError}
            />
          </motion.div>
        ) : (
          <motion.div
            key="fields"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ContactFieldsStage
              form={props.form}
              theme={props.theme}
              isScheduling={stage === 'scheduling'}
              onSchedulingStart={(next) => {
                setEmail(next.email);
                setSubmissionId(next.submissionId);
                setStage('scheduling');
              }}
              onSchedulingFinished={() => setStage('questions')}
              onSubmitError={props.onSubmitError}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ContactFieldsStage({
  form,
  theme,
  isScheduling,
  onSchedulingStart,
  onSchedulingFinished,
  onSubmitError,
}: {
  form: SanityContactForm;
  theme?: 'light' | 'dark';
  isScheduling: boolean;
  onSchedulingStart: (next: { email: string; submissionId: string }) => void;
  onSchedulingFinished: () => void;
  onSubmitError?: () => void;
}) {
  const { asPath } = useRouter();
  const pending = usePendingContactSubmission();
  const { airgap } = useConsentManager();
  const analytics = useAnalytics();
  const fireConversionEvent = useConversionEvent();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { initSchedulingModal, waitForSchedulingFinished, schedulingState } =
    useChiliPiperModal();

  const analyticsId = generateAnalyticsId(form.formTitle, asPath);

  const { methods, getMetadata } = useFormMetadata({
    hubspotCampaignId: form.hubspotCampaignId,
    hubspotConvertingFormType: form.hubspotConvertingFormType,
    hubspotEmailSubscriptionIds: form.hubspotEmailSubscriptionIds,
    trackingEventName: form.trackingEventName,
    formFieldsSchema: contactFieldsSchema,
    formTitle: form.formTitle,
    path: asPath,
    defaultValues: {
      email: pending?.email ?? '',
      submissionId: pending?.submissionId,
      consent: true,
    },
  });

  useEffect(() => methods.setValue('consent', true), [methods]);

  useEffect(() => {
    if (!pending) {
      return;
    }
    methods.setValue('email', pending.email);
    methods.setValue('submissionId', pending.submissionId);
  }, [pending, methods]);

  const handleSubmit: SubmitHandler<TContactFormFields> = async (fields) => {
    const trackingPurposes = Object.fromEntries(
      Object.keys(airgap?.getConsent().purposes ?? {}).map((purpose) => [
        purpose,
        airgap?.isConsented(new Set([purpose])) ?? false,
      ]),
    );
    // Homepage creates the submission without LeadIQ; enrich here so ChiliPiper
    // still gets company/geo. Direct /contact visits also enrich.
    const shouldEnrich = !pending?.enriched;

    try {
      const recaptchaToken =
        executeRecaptcha &&
        (await executeRecaptcha(snakeCase(form.trackingEventName)));
      const metadata = await getMetadata(recaptchaToken);

      const response = await submitLead(
        {
          ...fields,
          submissionId: fields.submissionId ?? pending?.submissionId,
          message: fields.message?.trim() ? fields.message : undefined,
        },
        shouldEnrich
          ? { ...metadata, shouldEnrich: true, trackingPurposes }
          : metadata,
        { analytics, isSubmissionComplete: false, forwardOnAbandon: true },
      );

      if ('error' in response) {
        console.error(response.error);
        methods.setError('root', {
          type: 'server',
          message: response.error,
        });
        onSubmitError?.();
        return;
      }

      fireConversionEvent(fields.email, form.trackingEventName, {
        formId: analyticsId,
        submissionId: response.submissionId,
        successfullyEnriched: shouldEnrich
          ? (response.enriched?.success ?? false)
          : Boolean(pending?.enriched),
        ...form.trackingEventProperties,
      });

      if (shouldEnrich) {
        analytics.track('Form Enrichment Attempted', {
          email: fields.email,
          wasSuccessful: response.enriched?.success ?? false,
          formId: analyticsId,
          submissionId: response.submissionId,
        });
      }

      setPendingContactSubmission({
        email: fields.email,
        submissionId: response.submissionId,
        enriched: shouldEnrich
          ? (response.enriched?.data ?? null)
          : (pending?.enriched ?? null),
      });

      const schedulingFields: TContactFormFields = {
        ...fields,
        ...(shouldEnrich ? response.enriched?.data : pending?.enriched),
        email: fields.email,
        firstName: fields.firstName,
        lastName: fields.lastName,
        title: fields.title,
        submissionId: response.submissionId,
        consent: true,
      };

      onSchedulingStart({
        email: fields.email,
        submissionId: response.submissionId,
      });
      await scheduleAfterSubmit({
        fields: schedulingFields,
        submissionId: response.submissionId,
        analyticsId,
        trackingEventName: form.trackingEventName,
        analytics,
        getMetadata,
        executeRecaptcha,
        initSchedulingModal,
        waitForSchedulingFinished,
      });
      onSchedulingFinished();
    } catch (err) {
      console.error(err);
      methods.setError('root', { type: 'server' });
      onSubmitError?.();
    }
  };

  const showSchedulingHint =
    isScheduling &&
    schedulingState.status === SchedulingStateEnum.ROUTING &&
    !schedulingState.showModal;

  return (
    <FormProvider {...methods}>
      <form
        name={analyticsId}
        onSubmit={methods.handleSubmit(handleSubmit)}
        className="grid grid-cols-2 gap-20"
      >
        <TextField
          name="firstName"
          label="First name"
          autoComplete="given-name"
          theme={theme}
          disabled={isScheduling}
          control={methods.control}
          half
        />
        <TextField
          name="lastName"
          label="Last name"
          autoComplete="family-name"
          theme={theme}
          disabled={isScheduling}
          control={methods.control}
          half
        />
        <TextField
          name="email"
          label="Work email"
          type="email"
          autoComplete="work email"
          theme={theme}
          disabled={isScheduling}
          control={methods.control}
        />
        <TextField
          name="title"
          label="Job title"
          autoComplete="organization-title"
          theme={theme}
          disabled={isScheduling}
          control={methods.control}
        />
        <FormField
          name="message"
          control={methods.control}
          defaultValue=""
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel className="sr-only">Message (optional)</FormLabel>
              <FormControl>
                <Textarea
                  theme={theme}
                  placeholder="Message (optional)"
                  disabled={isScheduling}
                  {...field}
                />
              </FormControl>
              <FormMessage className="legal-text mt-4 text-flame-06" />
            </FormItem>
          )}
        />
        <FormErrorMessage
          formState={methods.formState}
          className="col-span-full"
        />
        {showSchedulingHint ? (
          <p className="legal-text col-span-full text-grayscale-05">
            Finding a time…
          </p>
        ) : (
          <Button
            className="col-span-full grid place-items-center"
            variant="form"
            formTheme={theme}
            type="submit"
            disabled={methods.formState.isSubmitting || isScheduling}
          >
            <span
              className={cn(
                'row-start-1 col-start-1',
                (methods.formState.isSubmitting || isScheduling) && 'opacity-0',
              )}
            >
              {form.submitButtonLabel}
            </span>
            {(methods.formState.isSubmitting || isScheduling) && (
              <LoadingSpinner
                formTheme={theme}
                className="row-start-1 col-start-1 absolute size-22"
              />
            )}
          </Button>
        )}
        {form.legalText && form.legalText.length > 0 ? (
          <BasicText
            className={cn(
              'legal-text col-span-full mt-10 font-light text-grayscale-05',
              theme === 'dark' && 'text-cloud',
            )}
            value={form.legalText}
          />
        ) : null}
      </form>
    </FormProvider>
  );
}

function FollowUpQuestions({
  form,
  theme,
  email,
  submissionId,
  onSubmitSuccess,
  onSubmitError,
}: {
  form: SanityContactForm;
  theme?: 'light' | 'dark';
  email: string;
  submissionId: string;
  onSubmitSuccess: () => void;
  onSubmitError?: () => void;
}) {
  const { asPath } = useRouter();
  const analytics = useAnalytics();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { bookedMeeting } = useChiliPiperModal();

  const { methods, getMetadata } = useFormMetadata({
    hubspotCampaignId: form.hubspotCampaignId,
    hubspotConvertingFormType: form.hubspotConvertingFormType,
    hubspotEmailSubscriptionIds: form.hubspotEmailSubscriptionIds,
    trackingEventName: form.trackingEventName,
    formFieldsSchema: followUpSchema,
    formTitle: form.formTitle,
    path: asPath,
    defaultValues: {
      email,
      submissionId,
      consent: true,
      businessNeeds: [],
    },
  });

  useEffect(() => methods.setValue('consent', true), [methods]);

  const handleSubmit: SubmitHandler<TContactFormFields> = async (fields) => {
    try {
      const recaptchaToken =
        executeRecaptcha &&
        (await executeRecaptcha(snakeCase(form.trackingEventName)));
      const metadata = await getMetadata(recaptchaToken);

      const response = await submitLead(
        {
          ...fields,
          email,
          submissionId,
          consent: true,
          chiliPiperMeeting: bookedMeeting?.eventId ? bookedMeeting : undefined,
        },
        metadata,
        { analytics, isSubmissionComplete: true },
      );

      if ('error' in response) {
        console.error(response.error);
        methods.setError('root', {
          type: 'server',
          message: response.error,
        });
        onSubmitError?.();
        return;
      }

      onSubmitSuccess();
    } catch (err) {
      console.error(err);
      methods.setError('root', { type: 'server' });
      onSubmitError?.();
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(handleSubmit)}
        className="flex w-full max-w-full flex-col gap-28"
      >
        <fieldset className="w-full min-w-0">
          <legend className="text-16 font-[440] mb-12">
            Tell us more about your business needs. Are you looking for any of
            the following? (Select all that apply)
          </legend>
          <FormField
            name="businessNeeds"
            control={methods.control}
            defaultValue={[]}
            render={({ field }) => (
              <FormItem className="w-full">
                <Listbox
                  name={field.name}
                  theme={theme}
                  multiple
                  placeholder="Select all that apply"
                  value={Array.isArray(field.value) ? field.value : []}
                  onChange={field.onChange}
                  options={BusinessNeedOptions.map((option) => ({
                    label: option,
                    value: option,
                  }))}
                />
                <FormMessage className="legal-text mt-4 text-flame-06" />
              </FormItem>
            )}
          />
        </fieldset>

        <SelectQuestion
          name="businessNeedIntent"
          legend="Which best describes your business needs:"
          options={BusinessNeedIntentOptions}
          theme={theme}
          control={methods.control}
        />

        <FormErrorMessage formState={methods.formState} />

        <Button
          className="grid w-full place-items-center"
          variant="form"
          formTheme={theme}
          type="submit"
          disabled={methods.formState.isSubmitting}
        >
          <span
            className={cn(
              'row-start-1 col-start-1',
              methods.formState.isSubmitting && 'opacity-0',
            )}
          >
            Submit
          </span>
          {methods.formState.isSubmitting && (
            <LoadingSpinner
              formTheme={theme}
              className="row-start-1 col-start-1 absolute size-22"
            />
          )}
        </Button>
      </form>
    </FormProvider>
  );
}

function TextField({
  name,
  label,
  theme,
  disabled,
  half,
  type = 'text',
  autoComplete,
  control,
}: {
  name: 'firstName' | 'lastName' | 'email' | 'title';
  label: string;
  theme?: 'light' | 'dark';
  disabled: boolean;
  half?: boolean;
  type?: React.HTMLInputTypeAttribute;
  autoComplete?: string;
  control: ReturnType<typeof useFormMetadata>['methods']['control'];
}) {
  return (
    <FormField
      name={name}
      control={control}
      defaultValue=""
      render={({ field }) => (
        <FormItem className={cn('col-span-full', half && 'l:col-span-1')}>
          <FormLabel className="sr-only">{label}</FormLabel>
          <FormControl>
            <Input
              theme={theme}
              type={type}
              placeholder={label}
              autoComplete={autoComplete}
              disabled={disabled}
              name={field.name}
              onBlur={field.onBlur}
              onChange={field.onChange}
              ref={field.ref}
              value={typeof field.value === 'string' ? field.value : ''}
            />
          </FormControl>
          <FormMessage className="legal-text mt-4 text-flame-06" />
        </FormItem>
      )}
    />
  );
}

function SelectQuestion({
  name,
  legend,
  options,
  theme,
  control,
}: {
  name: 'businessNeedIntent';
  legend: string;
  options: readonly string[];
  theme?: 'light' | 'dark';
  control: ReturnType<typeof useFormMetadata>['methods']['control'];
}) {
  return (
    <fieldset className="w-full min-w-0">
      <legend className="text-16 font-[440] mb-12">{legend}</legend>
      <FormField
        name={name}
        control={control}
        render={({ field }) => (
          <FormItem className="w-full">
            <Listbox
              name={field.name}
              theme={theme}
              placeholder="Select an option"
              value={typeof field.value === 'string' ? field.value : ''}
              onChange={field.onChange}
              options={options.map((option) => ({
                label: option,
                value: option,
              }))}
            />
            <FormMessage className="legal-text mt-4 text-flame-06" />
          </FormItem>
        )}
      />
    </fieldset>
  );
}

export function ContactForm(props: Props) {
  return (
    <ChiliPiperProvider
      enableScheduling
      routerId={CONTACT_CHILI_PIPER_ROUTER_ID}
    >
      <ContactFormInner {...props} />
    </ChiliPiperProvider>
  );
}
